import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { PackageIcon, EyeIcon, UsersIcon, TrendingUpIcon, ArrowRightIcon, PlusIcon, BotIcon, LayersIcon, CarIcon, Share2Icon } from 'lucide-react';
import { useBusiness, getHubTabs } from './BusinessContext';
import { SetupGuide } from './hub/SetupGuide';
interface BusinessDashboardProps {
  onNavigate?: (tab: string) => void;
}
export function BusinessDashboard({
  onNavigate
}: BusinessDashboardProps) {
  const {
    hub,
    orderingEnabled
  } = useBusiness();
  const tabs = getHubTabs(hub?.type ?? 'generic');
  const inventoryLabel = tabs[1];
  const businessName = hub?.name || 'there';
  // Mock Data organized around user categories
  const categories = [{
    name: 'SUVs',
    listings: 42,
    views: '12.4k',
    leads: 156,
    trend: '+14%'
  }, {
    name: 'Sedans',
    listings: 28,
    views: '8.2k',
    leads: 89,
    trend: '+5%'
  }, {
    name: 'Hatchbacks',
    listings: 15,
    views: '3.1k',
    leads: 24,
    trend: '-2%'
  }];
  const kpis = [{
    label: `Total ${inventoryLabel}`,
    value: '85',
    trend: '+4'
  }, {
    label: 'Total Views',
    value: '23.7k',
    trend: '+12%'
  }, {
    label: 'New Leads',
    value: '269',
    trend: '+8%'
  }, {
    label: 'Conversion',
    value: '3.2%',
    trend: '+0.4%'
  }];
  const topPerforming = [{
    id: 1,
    title: 'Toyota Prado GXR 2023',
    category: 'SUVs',
    views: '1.2k',
    leads: 45,
    img: 'https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=200'
  }, {
    id: 2,
    title: 'Nissan Patrol V8 2022',
    category: 'SUVs',
    views: '980',
    leads: 38,
    img: 'https://images.unsplash.com/photo-1580273916550-e323be2ae537?auto=format&fit=crop&q=80&w=200'
  }, {
    id: 3,
    title: 'Honda Accord Sport 2023',
    category: 'Sedans',
    views: '850',
    leads: 29,
    img: 'https://images.unsplash.com/photo-1606152421802-db97b9c7a11b?auto=format&fit=crop&q=80&w=200'
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 15
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24
      }
    }
  };
  // Graceful Empty State
  if (categories.length === 0) {
    return <motion.div variants={containerVariants} initial="hidden" animate="show" className="max-w-3xl mx-auto text-center py-24 px-4" data-id="element-538" data-mp="1tgyeszdbj4pr">
        <motion.div variants={itemVariants} className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-8 border border-slate-100 shadow-sm" data-id="element-539" data-mp="10yawtstmc5sw">
          <PackageIcon className="w-10 h-10 text-slate-300" data-id="element-540" data-mp="37n34gqk1uk0w" />
        </motion.div>
        <motion.h2 variants={itemVariants} className="font-display font-bold text-3xl text-dark-navy mb-4" data-id="element-541" data-mp="3cel8jnhdd0q3">
          Welcome to your Business Hub
        </motion.h2>
        <motion.p variants={itemVariants} className="text-slate-500 text-lg mb-10 max-w-lg mx-auto leading-relaxed" data-id="element-542" data-mp="33lhx1kqaaxy3">
          Add your first {inventoryLabel.toLowerCase()} and Talab opens your AI
          shop, answers customers for you, and collects every order in one
          place.
        </motion.p>
        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-543" data-mp="3hcd0ghi0kmua">
          <button onClick={() => onNavigate?.(inventoryLabel)} className="w-full sm:w-auto px-6 py-3.5 bg-primary-indigo text-white font-semibold rounded-xl hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 shadow-sm" data-id="element-544" data-mp="25xzexjaqgjhf">
            <PlusIcon className="w-5 h-5" data-id="element-545" data-mp="2q8tx08zqo1t3" /> Add {inventoryLabel}
          </button>
          <button onClick={() => onNavigate?.('My Shop')} className="w-full sm:w-auto px-6 py-3.5 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-colors flex items-center justify-center gap-2 shadow-sm" data-id="element-546" data-mp="2lepo3k6wxjpd">
            <Share2Icon className="w-5 h-5 text-slate-400" data-id="element-547" data-mp="3951tooeor4cs" /> Set up my shop
          </button>
        </motion.div>
      </motion.div>;
  }
  // Active Populated State
  return <motion.div variants={containerVariants} initial="hidden" animate="show" className="space-y-10 pb-12 max-w-6xl mx-auto" data-id="element-548" data-mp="303u896x9py04">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4" data-id="element-549" data-mp="1oq9m2omm9hvd">
        <div data-id="element-550" data-mp="1jqnbkmy6afq9">
          <h2 className="font-display font-bold text-2xl text-dark-navy mb-1.5" data-id="element-551" data-mp="1jq409p312pl7">
            Good morning, {businessName}
          </h2>
          <p className="text-sm text-slate-500" data-id="element-552" data-mp="19o9t4yo6du17">
            Here's how your shop, leads and orders are doing today.
          </p>
        </div>
      </div>

      {/* Until the business is ready, the path comes before the numbers */}
      {onNavigate && <SetupGuide catalogLabel={inventoryLabel} onNavigate={onNavigate} data-id="element-553" data-mp="1oc4nsklqu8gx" />}

      {/* KPI Strip */}
      <motion.div variants={itemVariants} className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-id="element-554" data-mp="1sdzmdhcai7r0">
        {kpis.map(kpi => <div key={kpi.label} className="bg-white rounded-3xl p-6 border border-slate-200/60 shadow-sm flex flex-col justify-between" data-id="element-555" data-mp="1xedt8w25nbng">
            <p className="text-sm font-medium text-slate-500 mb-3" data-id="element-556" data-mp="279yswnjr3zu1">
              {kpi.label}
            </p>
            <div className="flex items-end gap-3" data-id="element-557" data-mp="7z7fxkvyirb6">
              <p className="text-3xl font-display font-bold text-dark-navy leading-none tracking-tight" data-id="element-558" data-mp="cvxv85zxtivj">
                {kpi.value}
              </p>
              {kpi.trend && <span className={`text-xs font-bold px-2 py-1 rounded-md mb-0.5 ${kpi.trend.startsWith('+') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-id="element-559" data-mp="3w2b30q3a71ax">
                  {kpi.trend}
                </span>}
            </div>
          </div>)}
      </motion.div>

      {/* Orders — today's commercial activity, one click from the full list.
          Only for businesses that actually take orders. */}
      <motion.div variants={itemVariants} className={`bg-white rounded-3xl p-6 border border-slate-200/60 shadow-sm ${orderingEnabled ? '' : 'hidden'}`} data-id="element-560" data-mp="itgemn75uqjs">
        <div className="flex items-center justify-between gap-4 mb-5" data-id="element-561" data-mp="slulfud3inct">
          <h3 className="font-display font-bold text-xl text-dark-navy flex items-center gap-2" data-id="element-562" data-mp="16kc61j7pfjqi">
            <PackageIcon className="w-5 h-5 text-primary-indigo" data-id="element-563" data-mp="12kbf4z3f5g2k" />
            Orders
          </h3>
          <button onClick={() => onNavigate?.('Orders')} className="inline-flex items-center gap-1.5 text-sm font-bold text-primary-indigo hover:text-indigo-700 transition-colors duration-150 ease-out" data-id="element-564" data-mp="1l7kl4v44w97r">
            View all
            <ArrowRightIcon className="w-4 h-4" data-id="element-565" data-mp="1c9gvpnq12sao" />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-4" data-id="element-566" data-mp="1h4e894minjsb">
          {[{
          label: 'New orders',
          value: '4',
          lead: true
        }, {
          label: 'Orders today',
          value: '12'
        }, {
          label: 'Revenue today',
          value: 'AED 18,500'
        }].map(s => <div key={s.label} data-id="element-567" data-mp="xg5dcmtl8zp4">
              <p className="text-sm font-medium text-slate-500" data-id="element-568" data-mp="jiql7abyadtg">{s.label}</p>
              <p className={`font-display font-bold mt-2 leading-none tracking-tight ${s.lead ? 'text-3xl text-primary-indigo' : 'text-2xl text-dark-navy'}`} data-id="element-569" data-mp="jkt83lht3qty">
                {s.value}
              </p>
            </div>)}
        </div>
      </motion.div>

      {/* Categories Section */}
      <motion.div variants={itemVariants} data-id="element-570" data-mp="uydckj6iv7xj">
        <div className="flex items-center justify-between mb-6" data-id="element-571" data-mp="uxbpfgc8euzg">
          <h3 className="font-display font-bold text-xl text-dark-navy flex items-center gap-2" data-id="element-572" data-mp="q0l8kqslrvcz">
            <LayersIcon className="w-5 h-5 text-primary-indigo" data-id="element-573" data-mp="l0ywikoigl5r" /> Your
            Categories
          </h3>
          <button onClick={() => onNavigate?.(inventoryLabel)} className="text-sm font-semibold text-primary-indigo hover:text-indigo-700 flex items-center gap-1 transition-colors" data-id="element-574" data-mp="g8xo59ugrel0">
            View all <ArrowRightIcon className="w-4 h-4" data-id="element-575" data-mp="7b58g4kty4x3" />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-576" data-mp="28wcr0gkoora">
          {categories.map(cat => <button key={cat.name} onClick={() => onNavigate?.(inventoryLabel)} className="text-left bg-white rounded-3xl p-6 border border-slate-200/60 shadow-sm hover:border-primary-indigo/30 hover:shadow-md transition-all group flex flex-col h-full" data-id="element-577" data-mp="3thtoh53rsl52">
              <div className="flex items-start justify-between mb-6" data-id="element-578" data-mp="2ix5s0l5tfqxw">
                <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center group-hover:bg-primary-indigo group-hover:text-white transition-colors" data-id="element-579" data-mp="2mx7siqzteebl">
                  <CarIcon className="w-6 h-6 text-primary-indigo group-hover:text-white transition-colors" data-id="element-580" data-mp="1v6tjb82qxq69" />
                </div>
                <span className={`text-xs font-bold px-2 py-1 rounded-md ${cat.trend.startsWith('+') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-id="element-581" data-mp="1z7lsk1bmbv2j">
                  {cat.trend}
                </span>
              </div>
              <h4 className="font-bold text-lg text-dark-navy mb-1" data-id="element-582" data-mp="1biu5focvy2wm">
                {cat.name}
              </h4>
              <p className="text-sm text-slate-500 mb-6" data-id="element-583" data-mp="i6zzci6t9fur">
                {cat.listings} active listings
              </p>

              <div className="mt-auto grid grid-cols-2 gap-4 pt-4 border-t border-slate-100" data-id="element-584" data-mp="n3ywzapoo4xp">
                <div data-id="element-585" data-mp="r86iz04rb6s8">
                  <p className="text-xs font-medium text-slate-400 mb-1 flex items-center gap-1" data-id="element-586" data-mp="r7oifrb2a6ei">
                    <EyeIcon className="w-3.5 h-3.5" data-id="element-587" data-mp="w56on9xp27it" /> Views
                  </p>
                  <p className="font-bold text-slate-700 text-base" data-id="element-588" data-mp="3vmo7x3wnlmyg">
                    {cat.views}
                  </p>
                </div>
                <div data-id="element-589" data-mp="8gdspdh2nzo7">
                  <p className="text-xs font-medium text-slate-400 mb-1 flex items-center gap-1" data-id="element-590" data-mp="8g5dhexh8i9u">
                    <UsersIcon className="w-3.5 h-3.5" data-id="element-591" data-mp="d7do2eajm9ol" /> Leads
                  </p>
                  <p className="font-bold text-slate-700 text-base" data-id="element-592" data-mp="3bxdr1ml68vyr">
                    {cat.leads}
                  </p>
                </div>
              </div>
            </button>)}
        </div>
      </motion.div>

      {/* Top Performing */}
      <motion.div variants={itemVariants} data-id="element-593" data-mp="39bwmfnyesdvb">
        <h3 className="font-display font-bold text-xl text-dark-navy mb-6 flex items-center gap-2" data-id="element-594" data-mp="34duv6fqtx88r">
          <TrendingUpIcon className="w-5 h-5 text-primary-indigo" data-id="element-595" data-mp="2zdyzr4w76p4t" /> Top
          Performing
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-596" data-mp="2vdg5l46u16zg">
          {topPerforming.map(item => <button key={item.id} onClick={() => onNavigate?.(inventoryLabel)} className="text-left bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm flex gap-4 items-center hover:border-primary-indigo/30 hover:shadow-md transition-all group" data-id="element-597" data-mp="2qeloc6oijqhg">
              <div className="w-20 h-20 bg-slate-100 rounded-xl flex-shrink-0 overflow-hidden" data-id="element-598" data-mp="2bqigssuto6qj">
                <img src={item.img} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-599" data-mp="27msuka1me18o" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-600" data-mp="1x28ob5twmioe">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 block" data-id="element-601" data-mp="1x308zllyqvzw">
                  {item.category}
                </span>
                <h4 className="font-bold text-sm text-dark-navy truncate mb-2 group-hover:text-primary-indigo transition-colors" data-id="element-602" data-mp="1o1ag0bcfnp00">
                  {item.title}
                </h4>
                <div className="flex items-center gap-3 text-xs font-medium text-slate-500" data-id="element-603" data-mp="2bthbz9e8xtyw">
                  <span className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded-md" data-id="element-604" data-mp="2gs0v721ggrnk">
                    <EyeIcon className="w-3.5 h-3.5" data-id="element-605" data-mp="21zh8td2iyyst" /> {item.views}
                  </span>
                  <span className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded-md" data-id="element-606" data-mp="26ybpo9gxmdaa">
                    <UsersIcon className="w-3.5 h-3.5" data-id="element-607" data-mp="2uoo2nyr1b07q" /> {item.leads}
                  </span>
                </div>
              </div>
            </button>)}
        </div>
      </motion.div>
    </motion.div>;
}